import Button from 'tio-reimagined/components/button';
import Chip from 'tio-reimagined/components/chip';
import { on } from '@ember/modifier';
import { HStack, Section, VStack } from 'tio-ui/components/layout';
import { Listbox } from 'tio-reimagined/components/listbox';
import { t } from 'ember-intl';
import Popover from 'tio-reimagined/components/popover';
import { and, not } from 'tio-ui/utilities';
import { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { type TBodyOptions } from 'tio-reimagined/components/table';
import Component from '@glimmer/component';
import DeleteAccountConfirmationModal from 'tio-common/components/account/delete-account-confirmation-modal';
import MarkPaidConfirmationModal from 'tio-common/components/account/mark-paid-confirmation-modal';
import safeFormatDate from 'tio-common/helpers/safe-format-date';
import safeFormatNumber from 'tio-common/helpers/safe-format-number';
import Table from 'tio-reimagined/components/table';
import type { TOC } from '@ember/component/template-only';
import type AccountModel from 'tio-common/models/account';
import type IntlService from 'ember-intl/services/intl';
import type LoanModel from 'tio-common/models/loan';
import type { NotificationsService } from 'tio-reimagined/components/notifications';
// This is for types. Anything we have custom may show an error.
import type StoreService from 'ember-data/store';
import type FeaturesService from 'tio-common/services/features';
import { PhCurrencyCircleDollar, PhDotsThreeVertical } from 'ember-phosphor-icons';
import { deactivateAccountRequest, markPaidRequest } from 'tio-common/requests/account';

interface TableRowSignature {
  Args: {
    loan: LoanModel;
    paid?: boolean;
    options: typeof TBodyOptions;
  };
}
const TableRow: TOC<TableRowSignature> = <template>
  <@options.tr>
    <@options.td>{{@loan.name}}</@options.td>
    <@options.td>{{@loan.loanTypeDisplayName}}</@options.td>
    <@options.td>
      {{#if @loan.repaymentPlan}}
        {{if @paid (t "accounts.paid_in_full") @loan.repaymentPlan}}
      {{else}}
        {{t "common.unknown"}}
      {{/if}}
    </@options.td>
    <@options.td>{{if @paid (t "accounts.paid_in_full") @loan.status}}</@options.td>
    <@options.td>
      {{safeFormatNumber @loan.originalAmount style="currency" currency="USD"}}
      {{t "common.on"}}
      {{safeFormatDate @loan.originalDate month="short" day="2-digit" year="numeric"}}
    </@options.td>
    <@options.td>
      {{t
        "common.accounts.interest_due"
        interestPercentage=@loan.interestRate
        interestDue=(safeFormatNumber @loan.interestBalance style="currency" currency="USD")
      }}
    </@options.td>
    <@options.td>
      {{safeFormatNumber @loan.principalBalance style="currency" currency="USD"}}
    </@options.td>
  </@options.tr>
</template>;

export interface AccountTableSignature {
  Args: {
    account: AccountModel;
    isReceivingContributions: boolean;
    onAccountChanged?: () => void;
  };
  Element: HTMLElement;
}
export default class AccountTable extends Component<AccountTableSignature> {
  @service declare notifications: NotificationsService;
  @service declare intl: IntlService;
  @service declare store: StoreService;
  @service declare features: FeaturesService;
  @tracked showMarkPaidConfirmation = false;
  @tracked showDeleteConfirmation = false;

  get account() {
    return this.args.account;
  }

  get accountNumber() {
    return this.account.number || 'unknown';
  }

  get loans() {
    return this.account.activeLoans.flat();
  }

  get unpaidLoans() {
    return this.loans.filter((l) => l.currentBalance);
  }

  get paidLoans() {
    return this.loans.filter((loan) => loan.status === 'PAID_IN_FULL');
  }

  get hasMultipleLoans() {
    return this.paidLoans.length + this.unpaidLoans.length > 1;
  }

  openMarkPaid = () => {
    this.showMarkPaidConfirmation = true;
  };

  closeMarkPaid = () => {
    this.showMarkPaidConfirmation = false;
  };

  markPaid = async () => {
    if (!this.args.account.id) return;
    try {
      await this.store.request(markPaidRequest(this.args.account.id));
      this.showMarkPaidConfirmation = false;
      this.notifications.add(this.intl.t('common.accounts.mark_paid_success'), {
        appearance: 'success',
      });
      this.args.onAccountChanged?.();
    } catch (error) {
      this.notifications.add(this.intl.t('common.accounts.mark_paid_error'), {
        appearance: 'error',
      });
      console.error('Error marking account as paid:', error);
    }
  };

  openDelete = () => {
    this.showDeleteConfirmation = true;
  };

  closeDelete = () => {
    this.showDeleteConfirmation = false;
  };

  deleteAccount = async () => {
    if (!this.args.account.id) return;
    try {
      await this.store.request(deactivateAccountRequest(this.args.account.id));
      this.showDeleteConfirmation = false;
      this.notifications.add(this.intl.t('common.accounts.delete_success'), {
        appearance: 'success',
      });
      this.args.onAccountChanged?.();
    } catch (error) {
      this.notifications.add(this.intl.t('common.accounts.delete_error'), {
        appearance: 'error',
      });
      console.error('Error deleting account:', error);
    }
  };

  @tracked menuOpen = false;

  openMenu = () => {
    this.menuOpen = true;
  };

  closeMenu = () => {
    this.menuOpen = false;
  };

  onMenuAction = (key: string) => {
    this.closeMenu();
    setTimeout(() => {
      if (key === 'mark_paid') {
        this.openMarkPaid();
      } else if (key === 'delete') {
        this.openDelete();
      }
    }, 250);
  };

  get accountActionsMenuFeature() {
    return this.features.isEnabled('accountActionsMenu');
  }

  <template>
    <VStack @collapsed={{true}}>
      <Section>
        <:header>
          <div class="flex items-center justify-between gap-x-4">
            <div class="flex items-center gap-x-4">
              <span>{{this.account.institutionName}}</span>
              {{#if @isReceivingContributions}}
                <Chip @intent="success">
                  <PhCurrencyCircleDollar @size="24" class="inline" />
                  {{t "common.accounts.receiving_contributions"}}
                </Chip>
              {{/if}}
            </div>
            {{#if (and (not this.account.isPaidOff) this.accountActionsMenuFeature)}}
              <Popover
                @placement="bottom-end"
                @isOpen={{this.menuOpen}}
                @onOpen={{this.openMenu}}
                @onClose={{this.closeMenu}}
                class="shrink-0"
              >
                <:trigger as |pop|>
                  <Button
                    @intent="primary"
                    @appearance="outlined"
                    {{pop.register}}
                    {{on "click" pop.toggle}}
                  >
                    <PhDotsThreeVertical @size="24" />
                  </Button>
                </:trigger>
                <:body>
                  <div class="w-[270px]">
                    <Listbox
                      @isKeyboardEventsEnabled={{true}}
                      @allowEmpty={{true}}
                      @onAction={{this.onMenuAction}}
                      @intent="danger"
                      as |l|
                    >
                      <l.Item @key="mark_paid">
                        {{t "common.accounts.mark_paid"}}
                      </l.Item>
                      <l.Item @key="delete">
                        {{t "common.accounts.delete"}}
                      </l.Item>
                    </Listbox>
                  </div>
                </:body>
              </Popover>
            {{/if}}
          </div>
        </:header>
        <:body>
          <div class="mb-2">
            <div class="block text-sm text-gray-700">
              {{t "accounts.last_update"}}
              {{safeFormatDate
                this.account.updatedAt
                month="short"
                day="2-digit"
                year="numeric"
                timeZone="UTC"
              }}
            </div>
            <div class="block text-sm text-gray-700">
              {{t "accounts.date_as_of"}}
              {{safeFormatDate
                this.account.providerSuccessAt
                month="short"
                day="2-digit"
                year="numeric"
                timeZone="UTC"
              }}
            </div>
          </div>
          <HStack @collapsed={{true}}>
            <Section>
              <:header>{{t "accounts.payment_details"}}</:header>
              <:body>
                <Table @isLoading={{false}}>
                  <:thead as |options|>
                    <options.tr>
                      <options.th>{{t "accounts.account_number"}}</options.th>
                      <options.th>{{t "accounts.address"}}</options.th>
                    </options.tr>
                  </:thead>
                  <:tbody as |options|>
                    <options.tr class="align-top">
                      <options.td>{{this.accountNumber}}</options.td>
                      <options.td>
                        {{#if @account.postalAddress}}
                          {{@account.postalAddress.addressLine1}}<br />
                          {{#if @account.postalAddress.addressLine2}}
                            {{@account.postalAddress.addressLine2}}<br />
                          {{/if}}
                          {{#if @account.postalAddress.addressLine3}}
                            {{@account.postalAddress.addressLine3}}<br />
                          {{/if}}
                          {{@account.postalAddress.city}}
                          {{@account.postalAddress.state}}
                          {{@account.postalAddress.postalCode}}
                        {{else}}
                          {{t "common.no_data"}}
                        {{/if}}
                      </options.td>
                    </options.tr>
                  </:tbody>
                </Table>
              </:body>
            </Section>
            <Section>
              <:header>{{t "accounts.account_details"}}</:header>
              <:body>
                <Table @isLoading={{false}}>
                  <:thead as |options|>
                    <options.tr>
                      <options.th>{{t "accounts.monthly_payment"}}</options.th>
                      <options.th>{{t "accounts.principal"}}</options.th>
                      <options.th>{{t "common.interest"}}</options.th>
                    </options.tr>
                  </:thead>
                  <:tbody as |options|>
                    <options.tr>
                      <options.td>
                        {{safeFormatNumber
                          this.account.providerRegularMonthlyPayment
                          style="currency"
                          currency="USD"
                        }}
                      </options.td>
                      <options.td>
                        {{safeFormatNumber
                          this.account.principalBalance
                          style="currency"
                          currency="USD"
                        }}
                      </options.td>
                      <options.td>
                        {{safeFormatNumber
                          this.account.interestBalance
                          style="currency"
                          currency="USD"
                        }}
                      </options.td>
                    </options.tr>
                  </:tbody>
                </Table>
              </:body>
            </Section>
          </HStack>
        </:body>
      </Section>
      <Section>
        {{! UNPAID LOANS - EXPANDABLE TABLE }}
        <Table @isLoading={{false}} ...attributes>
          <:thead as |options|>
            <options.tr>
              <options.th>{{t "common.name"}}</options.th>
              <options.th>
                <Popover @triggerMode="hover">
                  <:trigger as |pop|>
                    <span {{pop.register}} {{pop.hoverHandlers}}>
                      <div class="flex items-center">
                        {{t "common.type"}}
                      </div>
                    </span>
                  </:trigger>
                  <:body>
                    <div class="p-2">
                      {{t "accounts.type_info"}}
                    </div>
                  </:body>
                </Popover>
              </options.th>
              <options.th>
                <Popover @triggerMode="hover">
                  <:trigger as |pop|>
                    <span {{pop.register}} {{pop.hoverHandlers}}>
                      <div class="flex items-center text-nowrap p-4">
                        {{t "accounts.repayment_plan"}}
                      </div></span>
                  </:trigger>
                  <:body>
                    <div class="p-2">
                      {{t "accounts.plan_info"}}
                    </div>
                  </:body>
                </Popover>
              </options.th>
              <options.th>
                <Popover @triggerMode="hover">
                  <:trigger as |pop|>
                    <span {{pop.register}} {{pop.hoverHandlers}}>
                      {{t "common.status"}}
                    </span>
                  </:trigger>
                  <:body>
                    <div class="p-2">
                      {{t "accounts.status_info"}}
                    </div>
                  </:body>
                </Popover>
              </options.th>
              <options.th>{{t "accounts.updated_date"}}</options.th>
              <options.th>
                <Popover @triggerMode="hover">
                  <:trigger as |pop|>
                    <span {{pop.register}} {{pop.hoverHandlers}}>
                      {{t "common.interest"}}</span>
                  </:trigger>
                  <:body>
                    <div class="p-2">
                      {{t "accounts.rate_info"}}
                    </div>
                  </:body>
                </Popover>
              </options.th>
              <options.th>{{t "accounts.principal"}}</options.th>
            </options.tr>
          </:thead>
          <:tbody as |options|>
            {{#each this.unpaidLoans as |loan|}}
              <TableRow @loan={{loan}} @options={{options}} />
            {{/each}}
            {{#each this.paidLoans as |loan|}}
              <TableRow @loan={{loan}} @paid={{true}} @options={{options}} />
            {{/each}}
            {{#if this.hasMultipleLoans}}
              <options.tr>
                <options.td colspan="5" />
                <options.td>
                  {{safeFormatNumber this.account.interestBalance style="currency" currency="USD"}}
                </options.td>
                <options.td>
                  {{safeFormatNumber this.account.principalBalance style="currency" currency="USD"}}
                </options.td>
              </options.tr>
            {{/if}}
          </:tbody>
        </Table>
      </Section>
    </VStack>

    <MarkPaidConfirmationModal
      @isOpen={{this.showMarkPaidConfirmation}}
      @isReceivingContributions={{@isReceivingContributions}}
      @onClose={{this.closeMarkPaid}}
      @onSubmit={{this.markPaid}}
    />

    <DeleteAccountConfirmationModal
      @isOpen={{this.showDeleteConfirmation}}
      @isReceivingContributions={{@isReceivingContributions}}
      @onClose={{this.closeDelete}}
      @onSubmit={{this.deleteAccount}}
    />
  </template>
}
